"use client"

import { createContext, useContext, useState, useEffect, type ReactNode } from "react"
import { useRouter, useParams, usePathname } from "next/navigation"
import axios from "axios"
import Cookies from "js-cookie"
import { User } from "@/types/user"

import apiClient from "@/lib/axios-client" 
import { setAuthContext, resolveAuthContextFromRole, type AuthContext } from "@/lib/axios-client"
import { API_ENDPOINTS } from "@/lib/api-endpoints"
import { buildUserScopeHash, COOKIE_KEYS, resolveActiveUserScopeCookieKey, resolveBootstrapScopeFromRole } from "@/lib/cookie-keys"

interface RoleContextType {
    user: User | null
    isAuthenticated: boolean
    isLoading: boolean
    login: () => Promise<void> 
    logout: () => Promise<void>
}

const RoleContext = createContext<RoleContextType | undefined>(undefined)

const clearFrontendPreferenceCookies = () => {
    const allCookies = Cookies.get()
    const preferencePrefix = `${COOKIE_KEYS.preferencesPrefix}_`
    const userBootstrapPrefix = `${COOKIE_KEYS.themeBootstrapUserPrefix}_`
    const bootstrapKeySet = new Set<string>(Object.values(COOKIE_KEYS.themeBootstrapByScope))

    Object.keys(allCookies).forEach((cookieKey) => {
        const shouldRemovePreference = cookieKey.startsWith(preferencePrefix)
        const shouldRemoveBootstrap = bootstrapKeySet.has(cookieKey)
        const shouldRemoveUserBootstrap = cookieKey.startsWith(userBootstrapPrefix)

        if (!shouldRemovePreference && !shouldRemoveBootstrap && !shouldRemoveUserBootstrap) return

        Cookies.remove(cookieKey, { path: "/" })
        Cookies.remove(cookieKey)
    })

    Cookies.remove(COOKIE_KEYS.activeUserScope, { path: "/" })
    Cookies.remove(COOKIE_KEYS.activeUserScope)

    Object.values(COOKIE_KEYS.activeUserScopeByRole).forEach((key) => {
        Cookies.remove(key, { path: "/" })
        Cookies.remove(key)
    })
}

const persistActiveUserScope = (userId?: number, role?: string) => {
    if (typeof userId !== "number" || !Number.isFinite(userId)) {
        Object.values(COOKIE_KEYS.activeUserScopeByRole).forEach((key) => {
            Cookies.remove(key, { path: "/" })
            Cookies.remove(key)
        })
        return
    }

    const scope = resolveBootstrapScopeFromRole(role)
    const cookieKey = resolveActiveUserScopeCookieKey(scope)
    const secure = typeof window !== "undefined" ? window.location.protocol === "https:" : false
    Cookies.set(cookieKey, buildUserScopeHash(userId), {
        sameSite: "lax",
        path: "/",
        secure,
    })
}

export function RoleProvider({ children }: { children: ReactNode }) {
    const router = useRouter()
    const params = useParams()
    const pathname = usePathname()
    const locale = typeof params?.lang === "string" ? params.lang : "ro" // Default la 'ro' dacă nu găsește nimic
    const [user, setUser] = useState<User | null>(null)
    const [isLoading, setIsLoading] = useState(true)

    const resolvePathAuthContext = (path?: string): AuthContext => {
        if (!path) return null

        if (path.includes("/dashboard/buyer")) return "BUYER"
        if (path.includes("/dashboard/supplier")) return "SUPPLIER"
        if (path.includes("/dashboard/admin") || path.includes("/admin")) return "ADMIN"
        if (path.includes("/dashboard/operator")) return "OPERATOR"
        if (path.includes("/portal")) return null

        return null
    }

    // Set initial auth context from URL path before any API calls
    useEffect(() => {
        const contextFromPath = resolvePathAuthContext(pathname)
        setAuthContext(contextFromPath)
    }, [pathname]);

    // --- FUNCȚIA DE BAZĂ: Cere profilul de la Backend ---
    const fetchUserProfile = async (options?: { silent?: boolean }) => {
        try {
            // Backend-ul știe cine ești din Cookie-ul HttpOnly trimis automat de browser
            // Asigură-te că ruta aceasta este corectă în backend-ul tău!
            const response = await apiClient.get(API_ENDPOINTS.USER_PROFILE);
            
            // Verificăm structura răspunsului (adaptăm în funcție de backend)
            // Dacă backend-ul trimite { success: true, data: { ...user } }
            if (response.data?.data) {
                setUser(response.data.data);
                persistActiveUserScope(response.data.data.id, response.data.data.role)
            } 
            // Fallback: Dacă backend-ul trimite { success: true, user: { ...user } }
            else if (response.data?.user) {
                setUser(response.data.user);
                persistActiveUserScope(response.data.user.id, response.data.user.role)
            }
        } catch (error: unknown) {
            const status = axios.isAxiosError(error) ? error.response?.status : undefined;
            
            // Log detailed error for debugging (except for expected 401/403)
            if (!options?.silent && status && status !== 401 && status !== 403) {
                // Errors are handled by the caller; no client-side logging of internal details
            }
            
            setUser(null);
            persistActiveUserScope(undefined)
        } finally {
            setIsLoading(false);
        }
    };

        // Sync auth context header for multi-role cookie selection
        useEffect(() => {
            const userRoleContext = resolveAuthContextFromRole(user?.role);
            if (userRoleContext) {
                setAuthContext(userRoleContext);
            } else {
                setAuthContext(resolvePathAuthContext(pathname));
            }
        }, [user?.role, pathname]);

        // 1. HYDRATION: La refresh (F5), cerem datele proaspete de la server
        useEffect(() => {
            const shouldFetchProfileOnLoad =
                pathname?.includes("/dashboard") ||
                pathname?.includes("/portal") ||
                pathname?.includes("/admin");

            if (!shouldFetchProfileOnLoad) {
                setIsLoading(false);
                return;
            }

            fetchUserProfile({ silent: true });
        }, [pathname]);

        // 2. LOGIN: Doar declanșăm citirea profilului
        // Formularul de login face POST-ul, apoi apelează funcția asta
        const login = async () => {
            setIsLoading(true);
            await fetchUserProfile(); // Așteptăm să vină datele reale din DB
            // isLoading devine false în finally-ul de la fetchUserProfile
        }

    // 3. LOGOUT
    const logout = async () => {
        try {
            // Cerem serverului să șteargă cookie-urile HttpOnly (Set-Cookie: ... Max-Age=0)
            await apiClient.post(API_ENDPOINTS.LOGOUT);
        } catch (error: unknown) {
            const status = axios.isAxiosError(error) ? error.response?.status : undefined

            // 401/403 la logout apar frecvent când sesiunea e deja invalidă sau blocată pe backend.
            // În ambele cazuri continuăm delogarea locală fără să tratăm ca eroare critică.
            if (status !== 401 && status !== 403) {
                console.error("Logout warning:", error)
            }
        } finally {
            // 1. Curățăm memoria locală
            setUser(null);
            clearFrontendPreferenceCookies()
            
            // 2. Redirect către Login
            router.push(`/${locale}/login`);
            router.refresh(); // Forțăm curățarea cache-ului Next.js
        }
    }

    return (
        <RoleContext.Provider value={{
            user,
            isAuthenticated: !!user,
            isLoading,
            login,
            logout
        }}>
            {children}
        </RoleContext.Provider>
    )
}

export function useRole() {
    const context = useContext(RoleContext)
    if (context === undefined) {
        throw new Error("useRole must be used within a RoleProvider")
    }
    return context
}